import { useEffect, useState } from 'react';
import { Film, FileText, GalleryHorizontal, Image as ImageIcon, Music } from 'lucide-react';
import { ContentKind } from '@mg/shared';
import { cn } from '../../lib/cn';
import { useThumbnailSrc } from '../../lib/useProxyImage';

/**
 * The little media thumbnail on a calendar post card: the actual image when the post has one,
 * else a kind-appropriate placeholder (video → film, audio → note, text → doc). Falls back to the
 * placeholder if the image URL fails to load (e.g. an expired SAS link).
 */
export function PostThumbnail({
  mediaKind,
  thumbnailUrl,
  className,
  iconClassName,
  fill = false,
}: {
  mediaKind: ContentKind | null;
  thumbnailUrl: string | null;
  className?: string;
  /** Placeholder glyph size — bump it up when the thumbnail box is large. */
  iconClassName?: string;
  /**
   * Fill the parent instead of drawing the small square: the parent sets the size and must be
   * `relative`. No corner rounding, so a card that clips its children rounds the outer corners.
   *
   * A prop, not a className, because a caller's size cannot beat the base `h-7 w-7`: `cn` only joins
   * classes and the stylesheet orders utilities of one property by name, so `h-7` beats `h-24`. That
   * is why the campaign card's thumbnail used to draw 28px whatever it asked for.
   */
  fill?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  const dim = cn(fill ? 'absolute inset-0 h-full w-full' : 'h-7 w-7 shrink-0 rounded', className);
  // Resolve proxied (/api/assets/:id/image) URLs to an authed object URL — a raw <img src> to the
  // proxy 401s. External URLs pass through unchanged.
  const src = useThumbnailSrc(thumbnailUrl);
  // Clear a stale load failure when the resolved image changes (fresh SAS URL / new post).
  useEffect(() => setErrored(false), [src]);

  if (src && !errored) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setErrored(true)}
        // bg-white shows only through transparent pixels: a dark-on-transparent upload stays
        // legible in the dark theme.
        className={cn(dim, 'bg-white object-cover')}
      />
    );
  }
  const Icon =
    mediaKind === ContentKind.DOCUMENT
      ? GalleryHorizontal
      : mediaKind === ContentKind.VIDEO
        ? Film
      : mediaKind === ContentKind.AUDIO
        ? Music
        : mediaKind === ContentKind.IMAGE
          ? ImageIcon
          : FileText;
  return (
    <span className={cn(dim, 'flex items-center justify-center bg-chip text-ink-faint')}>
      <Icon className={cn('h-3.5 w-3.5', iconClassName)} aria-hidden />
    </span>
  );
}
